import React, { ChangeEvent } from 'react'

import { useMutation } from '@apollo/client'
import {
  IconChartPie,
  IconCopy,
  IconGitFork,
  IconInfoCircle,
  IconPlus,
  IconRestore,
  IconTrash,
} from '@tabler/icons-react'
import { ConfigProvider, Menu, Table, Tooltip } from 'antd'
import { partition } from 'lodash'
import moment from 'moment'
import styled from 'styled-components'

import {
  calculateHours,
  cloneValue,
  cloneWorkerAttendances,
  diffHours,
} from './workerAttendanceHelpers'

import { Button } from '@/components/shared-components/BUI/Button'
import { RangePicker } from '@/components/shared-components/BUI/DatePicker'
import { Dropdown } from '@/components/shared-components/BUI/Dropdown'
import { Input } from '@/components/shared-components/BUI/Input'
import { Modal } from '@/components/shared-components/BUI/Modal'
import { Select } from '@/components/shared-components/BUI/Select'
import { Text } from '@/components/shared-components/BUI/Text'
import Ellipsis from '@/components/shared-components/EllipsisDropdown'
import Flex from '@/components/shared-components/Flex'
import config from '@/config/index'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { MANUAL_SIGN_OFF } from '@/modules/signOns/graphQL'
import {
  AttendanceValue,
  HourConfig,
  WorkerAttendance,
} from '@/modules/templates/types/blueprint'

import { buildPassTheme } from 'src/styles/theme'

import {
  ManuallySignOffUiMutation,
  ManuallySignOffUiMutationVariables,
} from 'types/api.graphql'

type TimeChange = [moment.Moment, moment.Moment]
type InputChange = ChangeEvent<HTMLInputElement>
type AutoMenuProps = { type: 'remove' | 'restore'; onAction: () => void }

const clearAttendance: Partial<WorkerAttendance> = {
  signOnId: undefined,
  signedOnAt: undefined,
  signedOffAt: undefined,
}

const TooltipWithLeftMargin = styled(Tooltip)`
  margin-left: 0.5rem !important;
`

const DurationTooltip = () => (
  <TooltipWithLeftMargin
    title={
      <Text>
        The preset hours can be changed to suit your business. Simply contact us
        at <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>{' '}
        and we can help configure it for you.
      </Text>
    }
  >
    <IconInfoCircle size={16} />
  </TooltipWithLeftMargin>
)

const getWorkType = (workType: string | string[]) => {
  if (Array.isArray(workType)) {
    return workType.join(', ')
  }
  return workType
}

const hourMenu = (setHours, hourConfig: HourConfig[]) => {
  if (hourConfig?.length > 0) {
    return (
      <Menu>
        {hourConfig.map((config) => (
          <Menu.Item key={config.label} onClick={() => setHours(config.hours)}>
            {config.label}
          </Menu.Item>
        ))}
      </Menu>
    )
  }

  return (
    <Menu>
      <Menu.Item onClick={() => setHours(7.6)} key="full">
        Full day
      </Menu.Item>
      <Menu.Item onClick={() => setHours(5.7)} key="threequarter">
        3/4 day
      </Menu.Item>
      <Menu.Item onClick={() => setHours(3.8)} key="half">
        Half day
      </Menu.Item>
      <Menu.Item onClick={() => setHours(1.9)} key="quarter">
        1/4 day
      </Menu.Item>
    </Menu>
  )
}

const hoursSelect = (setHours, hourConfig: HourConfig[]) => (
  <Dropdown overlay={hourMenu(setHours, hourConfig)} trigger={['click']}>
    <IconChartPie size={16} />
  </Dropdown>
)

const manualMenu = (onDelete, duplicate, split) => (
  <Menu>
    <Menu.Item onClick={duplicate} key="duplicate">
      <IconCopy size={16} /> Duplicate
    </Menu.Item>
    <Menu.Item onClick={split} key="split">
      <IconGitFork size={16} rotate={90} /> Split
    </Menu.Item>
    <Menu.Item danger onClick={onDelete} key="remove">
      <IconTrash size={16} /> Remove
    </Menu.Item>
  </Menu>
)

const autoMenu = ({ type, onAction }: AutoMenuProps) => (
  <Menu>
    <Menu.Item danger={type === 'remove'} onClick={onAction} key={type}>
      {type === 'remove' ? <IconTrash size={16} /> : <IconRestore size={16} />}
      {type === 'remove' ? ' Remove' : ' Restore'}
    </Menu.Item>
  </Menu>
)

const getWorkerSignOnOff = (
  attendance: WorkerAttendance
): [moment.Moment, moment.Moment] => {
  const signedOnAt = attendance.signedOnAt
    ? moment(attendance.signedOnAt)
    : null

  const signedOffAt = attendance.signedOffAt
    ? moment(attendance.signedOffAt)
    : null

  return [signedOnAt, signedOffAt]
}

type Worker = {
  id: string
  name: string
  subcontractorId: string
}

type Props = {
  subcontractorId: string
  subbieName?: string
  value: AttendanceValue
  workers: Worker[]
  hourConfig: HourConfig[]
  onChange: (value: AttendanceValue) => void
  closeModal: () => void
}

export const WorkerAttendanceModal: React.FC<Props> = ({
  subcontractorId,
  subbieName,
  value,
  workers,
  hourConfig,
  onChange,
  closeModal,
}) => {
  const mobile = useBreakpoint('md')

  const [manuallySignOff, { loading: signOffLoading }] = useMutation<
    ManuallySignOffUiMutation,
    ManuallySignOffUiMutationVariables
  >(MANUAL_SIGN_OFF)

  if (subcontractorId === undefined) return null

  const [allAutoAttendance, manualAttendance] = partition(
    value.subcontractorAttendances.find(
      (s) => s.subcontractorId === subcontractorId
    ).workerAttendance,
    (w) => !w.manuallyCreated
  )

  // remove any deleted auto attendances
  const autoAttendance = allAutoAttendance.filter(
    (a) =>
      !value.overrides.some(
        ({ workerId, deleted }) => workerId === a.workerId && deleted === true
      )
  )

  const deletedAttendance = allAutoAttendance.filter((a) =>
    value.overrides.some(
      ({ workerId, deleted }) => workerId === a.workerId && deleted === true
    )
  )

  /** MANUAL WORKER FUNCTIONS */
  const updateManualWorker = (
    index: number,
    data: Partial<WorkerAttendance>
  ) => {
    const newManualAttendances = cloneWorkerAttendances(manualAttendance).map(
      (worker, workerIndex) => ({
        ...worker,
        ...(workerIndex === index ? data : {}),
      })
    )

    const newValue = cloneValue(value)

    newValue.subcontractorAttendances = newValue.subcontractorAttendances.map(
      (subbie) => ({
        ...subbie,
        workerAttendance:
          subbie.subcontractorId === subcontractorId
            ? [...autoAttendance, ...newManualAttendances]
            : subbie.workerAttendance,
      })
    )

    onChange(newValue)
  }

  const setManualWorkerId = (index: number) => (workerId: string) => {
    updateManualWorker(index, { workerId })
  }

  const setManualDuration = (index: number) => (e: InputChange) => {
    updateManualWorker(index, { hours: parseFloat(e.target.value) })
  }

  const setManualHours = (index: number) => (hours: number) => {
    updateManualWorker(index, { hours })
  }

  const setManualComments = (index: number) => (e: InputChange) => {
    updateManualWorker(index, { workType: e.target.value })
  }

  const addManualAttendance = () => {
    const newValue = cloneValue(value)

    const subbie = newValue.subcontractorAttendances.find(
      (s) => s.subcontractorId === subcontractorId
    )

    subbie.workerAttendance.push({ manuallyCreated: true } as WorkerAttendance)

    onChange(newValue)
  }

  const deleteManualAttendance = (index: number) => () => {
    const newManualAttendances = cloneWorkerAttendances(manualAttendance)
    newManualAttendances.splice(index, 1)

    const newValue = cloneValue(value)

    const subbie = newValue.subcontractorAttendances.find(
      (s) => s.subcontractorId === subcontractorId
    )

    subbie.workerAttendance = [...autoAttendance, ...newManualAttendances]
    onChange(newValue)
  }

  const duplicateManualAttendance = (index: number) => () => {
    const newManualAttendances = cloneWorkerAttendances(manualAttendance)
    newManualAttendances.splice(index + 1, 0, {
      ...newManualAttendances[index],
      ...clearAttendance,
    })

    const newValue = cloneValue(value)
    const subbie = newValue.subcontractorAttendances.find(
      (s) => s.subcontractorId === subcontractorId
    )
    subbie.workerAttendance = [...autoAttendance, ...newManualAttendances]
    onChange(newValue)
  }

  const splitManualAttendance = (index: number) => () => {
    const newManualAttendances = cloneWorkerAttendances(manualAttendance)
    const attendance = { ...newManualAttendances[index] } as WorkerAttendance
    const splitAttendances = [
      { ...attendance, hours: attendance.hours / 2 || 0, ...clearAttendance },
      { ...attendance, hours: attendance.hours / 2 || 0, ...clearAttendance },
    ] as WorkerAttendance[]
    newManualAttendances.splice(index, 1, ...splitAttendances)

    const newValue = cloneValue(value)
    const subbie = newValue.subcontractorAttendances.find(
      (s) => s.subcontractorId === subcontractorId
    )

    subbie.workerAttendance = [...autoAttendance, ...newManualAttendances]
    onChange(newValue)
  }

  /** AUTO WORKER FUNCTIONS */
  const setAutoDuration = (index: number) => (e: InputChange) => {
    return setAutoHours(index)(parseFloat(e.target.value))
  }

  const setAutoHours = (index: number) => (hours: number) => {
    if (isNaN(hours)) {
      hours = 0
    }

    const newAutoAttendances = cloneWorkerAttendances(autoAttendance)
    const attendance = newAutoAttendances[index]

    if (attendance.signOnId && attendance.signedOnAt) {
      const signOnMoment = moment(attendance.signedOnAt)
      const newSignOffAt = signOnMoment
        .clone()
        .add(hours, 'hours')
        .toISOString()

      attendance.signedOffAt = newSignOffAt
      attendance.hours = diffHours(signOnMoment, moment(newSignOffAt))

      manuallySignOff({
        variables: {
          input: {
            signOnId: attendance.signOnId,
            signOnDate: attendance.signedOnAt,
            signOffDate: newSignOffAt,
          },
        },
      })

      const newValue = cloneValue(value)
      const subbie = newValue.subcontractorAttendances.find(
        (s) => s.subcontractorId === subcontractorId
      )
      subbie.workerAttendance = [...newAutoAttendances, ...manualAttendance]
      onChange(newValue)
    }
  }

  const signAutoOnOff =
    (index: number) =>
    ([signOnAt, signOffAt]: TimeChange) => {
      const newAutoAttendances = cloneWorkerAttendances(autoAttendance)
      const attendance = newAutoAttendances[index]

      if (attendance.signOnId) {
        const correctSignOffAt = moment(signOnAt).set({
          hours: signOffAt.hours(),
          minutes: signOffAt.minutes(),
        })

        manuallySignOff({
          variables: {
            input: {
              signOnId: attendance.signOnId,
              signOnDate: signOnAt.toISOString(),
              signOffDate: correctSignOffAt.toISOString(),
            },
          },
        })

        attendance.signedOnAt = signOnAt.toISOString()
        attendance.signedOffAt = correctSignOffAt.toISOString()
        attendance.hours = diffHours(signOnAt, correctSignOffAt)

        const newValue = cloneValue(value)
        const subbie = newValue.subcontractorAttendances.find(
          (s) => s.subcontractorId === subcontractorId
        )
        subbie.workerAttendance = [...newAutoAttendances, ...manualAttendance]
        onChange(newValue)
      }
    }

  const setAutoComments = (workerId: string) => (e: InputChange) => {
    const newValue = cloneValue(value)

    const index = newValue.overrides?.findIndex((o) => o.workerId === workerId)

    if (index >= 0) {
      newValue.overrides[index] = {
        ...newValue.overrides[index],
        comments: e.target.value,
      }
    } else {
      newValue.overrides.push({ workerId, comments: e.target.value })
    }

    onChange(newValue)
  }

  const deleteAutoAttendance = (workerId: string) => () => {
    const newValue = cloneValue(value)

    const index = newValue.overrides?.findIndex((o) => o.workerId === workerId)

    if (index >= 0) {
      newValue.overrides[index] = {
        ...newValue.overrides[index],
        deleted: true,
      }
    } else {
      newValue.overrides.push({ workerId, deleted: true })
    }

    onChange(newValue)
  }

  const restoreAutoAttendance = (workerId: string) => () => {
    const newValue = cloneValue(value)
    const index = newValue.overrides?.findIndex((o) => o.workerId === workerId)

    if (index >= 0) {
      newValue.overrides[index] = {
        ...newValue.overrides[index],
        deleted: false,
      }
    } else {
      newValue.overrides.push({ workerId, deleted: false })
    }

    onChange(newValue)
  }

  const autoRenderEmpty = () => (
    <div style={{ textAlign: 'center' }}>
      <Text secondary="_600">No sign ons found for {subbieName}</Text>
    </div>
  )

  const manualRenderEmpty = () => (
    <div style={{ textAlign: 'center' }}>
      <Text secondary="_600">No manual attendances for {subbieName}</Text>
    </div>
  )

  return (
    <Modal
      open
      centered
      width={buildPassTheme.modal.large}
      onOk={closeModal}
      onCancel={closeModal}
      confirmLoading={signOffLoading}
      title={subbieName ? `${subbieName} attendance` : 'Worker Attendance'}
      destroyOnClose
      footer={[
        <Text key="hours" secondary="_600" style={{ marginRight: '1rem' }}>
          Total hours:{' '}
          {calculateHours(
            value.subcontractorAttendances.find(
              (s) => s.subcontractorId === subcontractorId
            ),
            value.overrides
          )}
        </Text>,
        <Button key="done" onClick={closeModal}>
          Close
        </Button>,
      ]}
    >
      <ConfigProvider renderEmpty={autoRenderEmpty}>
        <Table
          scroll={{ x: 'max-content' }}
          dataSource={autoAttendance}
          pagination={false}
          size="middle"
          rowKey="workerId"
          title={() => (
            <>
              <strong>Automatic attendance</strong> records pulled from sign
              ons.
              <br />
              <Text secondary="_600">
                <i>
                  Note: Modifying these will adjust the worker&apos;s sign on
                  times.
                </i>
              </Text>
            </>
          )}
          columns={[
            {
              title: 'Worker',
              dataIndex: 'workerId',
              width: 200,
              render: (workerId) =>
                workers.find((w) => w.id === workerId)?.name || 'unknown',
            },
            {
              title: 'Sign On/Off',
              width: 220,
              render: (_, attendance, index) => (
                <RangePicker
                  picker="time"
                  suffixIcon={null}
                  allowClear={false}
                  disabled={!attendance.signOnId}
                  format={'hh:mm a'}
                  style={{ background: 'transparent' }}
                  value={getWorkerSignOnOff(attendance)}
                  onChange={signAutoOnOff(index)}
                />
              ),
            },
            {
              title: () => (
                <>
                  Hours <DurationTooltip />
                </>
              ),
              width: 140,
              render: (_, attendance, index) => (
                <Input
                  type="number"
                  placeholder="hours"
                  onChange={setAutoDuration(index)}
                  value={attendance.hours}
                  addonAfter={hoursSelect(setAutoHours(index), hourConfig)}
                  min={0}
                />
              ),
            },
            {
              title: 'Comments',
              render: (_, attendance) => (
                <Input
                  placeholder="Comments"
                  style={{ width: '100%' }}
                  onChange={setAutoComments(attendance.workerId)}
                  value={
                    value.overrides?.find(
                      ({ workerId }) => workerId === attendance.workerId
                    )?.comments
                  }
                />
              ),
            },
            {
              title: '',
              dataIndex: 'workerId',
              render: (workerId) => (
                <Ellipsis
                  menu={autoMenu({
                    type: 'remove',
                    onAction: deleteAutoAttendance(workerId),
                  })}
                />
              ),
            },
          ]}
        />
      </ConfigProvider>

      <br />

      {deletedAttendance.length > 0 && (
        <ConfigProvider>
          <Table
            scroll={{ x: 'max-content' }}
            dataSource={deletedAttendance}
            pagination={false}
            rowKey="workerId"
            size="small"
            title={() => (
              <>
                Records <strong>removed</strong> from the attendance list.
                <br />
                <Text secondary="_600">
                  <i>
                    Note: You can restore these records using the actions menu.
                  </i>
                </Text>
              </>
            )}
            columns={[
              {
                title: 'Worker',
                dataIndex: 'workerId',
                width: 200,
                render: (workerId) =>
                  workers.find((w) => w.id === workerId)?.name || 'unknown',
              },
              {
                title: 'Sign On/Off',
                width: 220,
                render: (_, attendance, index) => (
                  <RangePicker
                    picker="time"
                    suffixIcon={null}
                    allowClear={false}
                    disabled
                    format={'hh:mm a'}
                    style={{ background: 'transparent' }}
                    value={getWorkerSignOnOff(attendance)}
                    onChange={signAutoOnOff(index)}
                  />
                ),
              },
              {
                title: () => (
                  <>
                    Hours <DurationTooltip />
                  </>
                ),
                width: 140,
                render: (_, attendance) => (
                  <Input
                    type="number"
                    placeholder="hours"
                    value={attendance.hours}
                    min={0}
                    disabled
                  />
                ),
              },
              {
                title: 'Comments',
                render: (_, attendance) => (
                  <Input
                    placeholder="Comments"
                    style={{ width: '100%' }}
                    onChange={setAutoComments(attendance.workerId)}
                    value={
                      value.overrides?.find(
                        ({ workerId }) => workerId === attendance.workerId
                      )?.comments
                    }
                  />
                ),
              },
              {
                title: '',
                dataIndex: 'workerId',
                render: (workerId) => (
                  <Ellipsis
                    menu={autoMenu({
                      type: 'restore',
                      onAction: restoreAutoAttendance(workerId),
                    })}
                  />
                ),
              },
            ]}
          />
        </ConfigProvider>
      )}

      <br />
      <br />
      <br />

      <ConfigProvider renderEmpty={manualRenderEmpty}>
        <Table
          scroll={{ x: 'max-content' }}
          dataSource={manualAttendance}
          pagination={false}
          rowKey="workerId"
          title={() => (
            <Flex
              alignItems={mobile ? 'start' : 'center'}
              justifyContent="between"
              flexDirection={mobile ? 'column' : 'row'}
            >
              <span>
                <strong>Manually created attendance</strong> within this Site
                Diary.
                <br />
                <Text secondary="_600">
                  <i>
                    Note: Adding/modifying these does not create any sign on
                    records.
                  </i>
                </Text>
              </span>
            </Flex>
          )}
          size="middle"
          columns={[
            {
              title: 'Worker',
              dataIndex: 'workerId',
              width: 200,
              render: (workerId, _, index) => (
                <Select
                  value={workerId}
                  style={{ width: '100%' }}
                  onChange={setManualWorkerId(index)}
                  placeholder="Select worker"
                  notFoundContent="No workers found"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (
                      (option as unknown as { children?: string })?.children ??
                      ''
                    )
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {workers.map((w) => (
                    <Select.Option value={w.id} key={w.id}>
                      {w.name}
                    </Select.Option>
                  ))}
                </Select>
              ),
            },
            {
              title: () => (
                <>
                  Hours <DurationTooltip />
                </>
              ),
              dataIndex: 'hours',
              width: 140,
              render: (hours, _, index) => (
                <Input
                  type="number"
                  placeholder="hours"
                  onChange={setManualDuration(index)}
                  addonAfter={hoursSelect(setManualHours(index), hourConfig)}
                  value={hours}
                  min={0}
                />
              ),
            },
            {
              title: 'Comments',
              render: (_, attendance, index) => (
                <Input
                  placeholder="Comments"
                  style={{ width: '100%' }}
                  onChange={setManualComments(index)}
                  value={getWorkType(attendance.workType)}
                />
              ),
            },
            {
              title: '',
              render: (_, __, index) => (
                <Ellipsis
                  menu={manualMenu(
                    deleteManualAttendance(index),
                    duplicateManualAttendance(index),
                    splitManualAttendance(index)
                  )}
                />
              ),
            },
          ]}
        />
        <br />
        <Button onClick={addManualAttendance} icon={<IconPlus size={16} />}>
          Add manual attendance
        </Button>
      </ConfigProvider>
    </Modal>
  )
}
